<script setup lang="ts">
import { ref, inject } from 'vue';
import { Button } from '@albank/vue-ui-kit';
import { useDevice } from '@/composables/useDevice';

const view = inject('view', 'mobile');
const { installationTab } = useDevice();

const activeTab = ref<'ios' | 'android'>(installationTab.value);

const stores = [
  {
    name: 'Google Play',
    text: 'Для всех телефонов',
    icon: '/assets/images/installation/google-play.png',
    link: 'https://play.google.com/store/apps/details?id=ru.albank.online.aebit&hl=ru'
  },
  {
    name: 'RuStore',
    text: 'Для всех телефонов',
    icon: '/assets/images/installation/ru-store.png',
    link: 'https://www.rustore.ru/catalog/app/ru.albank.online.aebit'
  },
  {
    name: 'App Gallery',
    text: 'Для телефонов Huawei',
    icon: '/assets/images/installation/app-gallery.png',
    link: 'https://appgallery.huawei.com/#/app/C104494399'
  }
];
</script>

<template>
  <div class="installation screen screen-container" id="installation">
    <h2
      v-motion
      :initial="{opacity: 0, y: 200}"
      :visibleOnce="{opacity: 1, y: 0, transition: {duration: 1500}}"
    >Как установить или обновить приложение</h2>

    <div
      v-motion
      class="installation__content"
      :initial="{opacity: 0, y: 100}"
      :visible-once="{opacity: 1, y: 0, transition: {duration: 1500, delay: 400}}"
    >
      <div class="installation__toggle">
        <button
          :class="activeTab == 'ios' ? 'active' : ''"
          @click="activeTab = 'ios'"
        >На iOS</button>
        <button
          :class="activeTab == 'android' ? 'active' : ''"
          @click="activeTab = 'android'"
        >На Android</button>
      </div>

      <div v-if="activeTab == 'ios'" class="installation__card office">
        <div class="office__text">
          <h4>Обратитесь в ближайший офис АЭБ</h4>
          <p>Сотрудники помогут скачать или обновить приложение, а также зарегистрироваться в нём</p>
          <a href="https://albank.ru/ru/offices/" target="_blank">
            <Button class="light-button button">Адреса</Button>
          </a>
        </div>
        <img src="/assets/images/installation/map-marker.svg" alt="Адреса офисов" class="office__image">
      </div>

      <div v-else class="installation__stores">
        <p class="installation__message">
          Установите или обновите приложение через магазин на вашем телефоне или скачайте установочный файл ниже
        </p>
        <a
          v-for="store in stores"
          :key="store.name"
          :href="store.link"
          target="_blank"
          class="installation__card store"
        >
          <img :src="store.icon" :alt="store.name" class="store__logo">
          <h4>{{ store.name }}</h4>
          <p>{{ store.text }}</p>
          <img 
            v-if="view == 'mobile'"
            class="store__arrow"
            src="/assets/icons/arrow-right-gray.svg" 
            alt="установить"/>
        </a>

        <div class="installation__card download">
          <div class="download__text">
            <h4 v-if="view == 'mobile'">Нет магазина приложений? Скачайте установочный файл</h4>
            <h4 v-else>Нет магазина приложений? Скачайте установочный файл</h4>
            <ol>
              <li v-if="view == 'mobile'">Нажмите кнопку <strong>Скачать</strong></li>
              <li v-else>Отсканируйте qr-код камерой вашего телефона</li>
              <li>Появится сообщение о ненадёжном источнике. Это стандартное предупреждение, с приложением всё в порядке</li>
              <li>Нажмите <strong>Всё равно скачать</strong>, а потом — <strong>Разрешить установку</strong></li>
            </ol>
            <a v-if="view == 'mobile'" href="https://albank.ru/app/AEBOnline.apk">
              <Button class="light-button button">Скачать</Button>
            </a>
            <aside class="download-warning">
              <img src="/assets/icons/shield.svg" alt="Осторожно" class="download-warning__icon">
              <p>
                Скачивайте приложение только на этой странице или в магазинах приложений. Не устанавливайте его по ссылкам из мессенджеров, соцсетей и писем — так действуют мошенники
              </p>
            </aside>
          </div>
          <div v-if="view != 'mobile'" class="download__images">
            <div class="download-qr">
              <img src="/assets/images/installation/qr-code.png" alt="QR-код для скачивания" class="download-qr__image">
              <p>
                Отсканируйте, чтобы скачать файл приложения
              </p>
            </div>
            <img src="/assets/images/installation/phone-picture.png" alt="АЭБ Онлайн" class="download__phone">
          </div>
        </div>
        <article class="installation-problem">
          <div class="installation-problem-text">
            <h3>
              Не получается установить или обновить?
            </h3>
            <p>
              Позвоните — подскажем по шагам
            </p>
            <div class="installation-problem-text-contacts">
              <a href="tel:88001003422">
                <img src="/assets/icons/call.svg" alt="Позвонить">
                <span>
                  8 800 100-34-22
                </span>
              </a>
              <a href="tel:+74112342222">
                <img src="/assets/icons/call.svg" alt="Позвонить">
                <span>
                  8 (4112) 34-22-22
                </span>
              </a>
            </div>
          </div>
          <img src="/assets/images/installation/chat.png" alt="Позвоните" class="installation-problem__image"/>
        </article>
      </div>
    </div>
  </div>
</template>


<style lang="scss" scoped>
.installation {
  gap: 60px;

  &__content {
    gap: 60px;
    width: 100%;
    display: flex;
    align-items: center;
    flex-direction: column;

    @media screen and (max-width: 800px) {
      gap: 40px;
    }
  }

  &__message {
    color: #1F2632;
    width: 100%;
    margin-bottom: 30px;
    text-align: center;

    @media screen and (max-width: 800px) {
      margin-bottom: 28px;
    }
  };

  &__toggle {
    padding: 4px;
    display: flex;
    border-radius: 100px;
    background: var(--color-mono-0);
    border: 1px solid var(--color-mono-100);

    button {
      border: none;
      cursor: pointer;
      transition: 0.3s;
      padding: 12px 24px;
      border-radius: 100px;
      background: transparent;
      color: var(--color-mono-900);
      font-size: var(--font-size-s);
      font-family: var(--default-font);

      &.active {
        color: var(--color-mono-0);
        background: var(--color-mono-900);
      }
    }
  }

  &__card {
    padding: 32px;
    border-radius: 32px;
    background: var(--color-mono-10);

    @media screen and (max-width: 800px) {
      padding: 20px;
      background: #F4F6F8;
    }
  }

  &__stores {
    gap: 30px;
    width: 100%;
    display: flex;
    flex-wrap: wrap;

    @media screen and (max-width: 800px) {
      gap: 12px;
    }
  }

  &-problem {
    background: #EBF4FE;
    display: flex;
    width: 100%;
    justify-content: space-between;
    border-radius: 32px;

    @media screen and (max-width: 800px) {
      padding: 24px;
      flex-direction: column-reverse;
    }

    &-text {
      padding: 40px;

      @media screen and (max-width: 800px) {
        padding: 0;
      }

      h3 {
        font-size: 34px;
        font-weight: 600;
        margin-bottom: 12px;

        @media screen and (max-width: 800px) {
          font-size: 24px;
          margin-bottom: 8px;
        }
      }

      p {
        font-size: 18px;
        color: #1F2632;
        margin-bottom: 32px;

        @media screen and (max-width: 800px) {
          font-size: 16px;
          margin-bottom: 20px;
        }
      }

      &-contacts {
        display: flex;
        gap: 80px;

        @media screen and (max-width: 800px) {
          flex-direction: column;
          gap: 12px;
        }

        a {
          display: flex;
          gap: 16px;
          font-size: 22px;
          font-weight: 500;
          color: #1F2632;

          @media screen and (max-width: 800px) {
            gap: 8px;
            font-size: 16px;
          }

          span {
            white-space: nowrap;
          }
        }
      }
    }

    &__image {
      width: 240px;

      @media screen and (max-width: 800px) {
        width: 100px;
      }
    }
  }
}

.office {
  gap: 40px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &__text {
    gap: 16px;
    display: flex;
    max-width: 60%;
    flex-direction: column;
    align-items: flex-start;
  }

  &__image {
    width: 96px;
  }

  @media screen and (max-width: 800px) {
    text-align: center;
    flex-direction: column-reverse;

    &__text {
      max-width: 100%;
      align-items: center;

      a,
      .button {
        width: 100%;
      }
    }
  }
}

.store {
  gap: 8px;
  display: flex;
  text-align: center;
  align-items: center;
  padding: 32px 20px;
  flex: 1 1 calc(33% - 16px);
  flex-direction: column;
  position: relative;

  &__logo {
    width: 60px;
    height: 60px;
    margin-bottom: 8px;
  }

  h4 {
    font-size: var(--font-size-m);
  }

  p {
    font-size: var(--font-size-s);
  }

  @media screen and (max-width: 800px) {
    flex: 1 1 100%;
  }

  &__arrow {
    position: absolute;
    top: 50%;
    right: 20px;
    margin-top: -11px;
  }
}

.download {
  gap: 59px;
  display: flex;
  flex: 1 1 100%;
  padding: 20px;

  &__text {
    gap: 16px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    ol {
      gap: 8px;
      margin: 0;
      display: flex;
      padding-left: 20px;
      flex-direction: column;
      
      @media screen and (min-width: 800px) {
        width: 465px;
      }

      li {
        font-size: var(--font-size-s);
        color: var(--color-mono-700);
        font-weight: var(--font-weight-light);
      }
    }
  }
  @media screen and (min-width: 800px) {
    padding: 48px;

    &__text {
      width: 550px;
    }
  }

  &-warning {
    display: flex;
    gap: 10px;
    border-radius: 16px;
    padding: 14px;
    background: #fff;
    color: var(--color-mono-700);

    @media screen and (min-width: 800px) {
      padding: 14px 16px;
      width: 500px;
    }

    &__icon {
      width: 20px;
      height: 20px;
    }

    p {
      font-size: 13px;
      font-weight: 400;
      line-height: 18px;

      @media screen and (min-width: 800px) {
        font-size: 14px;
        line-height: 20px;
      }
    }
  }

  &__images {
    gap: 24px;
    display: flex;
    flex-shrink: 0;
    align-items: center;
  }

  &-qr {
    width: 208px;

    p {
      font-size: 14px;
      line-height: 20px;
      text-align: center;
      font-weight: 400;
    }
  }

  &-qr__image {
    width: 140px;
    border-radius: 12px;
    margin-bottom: 12px;
    margin-left: auto;
    margin-right: auto;
  }

  &__phone {
    width: 150px;
  }  

  @media screen and (min-width: 800px) {
    &__images {
      position: relative;
      width: 440px;
    }

    &-qr__image {
      width: 186px;
    }

      &__phone {
        position: absolute;
        top: -50px;
        left: 237px;
        width: 194px;
      }
  }

  @media screen and (max-width: 800px) {
    &__text {
      width: 100%;

      h4 {
        width: 100%;
        text-align: center;
      }

      a,
      .button {
        width: 100%;
      }
    }
  }
}
</style>
